import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Button } from "../components/button";
import { Dialog, DialogTitle } from "../components/dialog";
import { Input } from "../components/input";
import { Textarea } from "../components/textarea";
import { Heading } from "../components/heading";
import { Text } from "../components/text";
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "../components/table";
import { Field, Label } from "../components/fieldset";
import { useUser } from "../components/UserContext";
import { UserPlusIcon, PencilSquareIcon, ArchiveBoxIcon } from "@heroicons/react/24/outline";
import { useClients, useData, useArchive } from "../contexts/DataContext";
import { getClientExpiryStatus } from "../utils/documentExpiry";
import { getClientIdentityStatus } from "../utils/identityMismatch";
import AssignedUsersBubbles from "../components/AssignedUsersBubbles";
import UserAssignmentSelector from "../components/UserAssignmentSelector";
import { logBitacora, computeUserAssignmentChanges } from "../utils/bitacora";
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from "../hooks/useFrozenColumns";
import { texto } from '../utils/busqueda';

const Clientes = () => {
  const navigate = useNavigate();
  const { user, userData, loading: userLoading, getUserEmail, getUserName } = useUser();
  const { clientsList, clientsLoading, addClient, updateClientAssignedUsers } = useClients();
  const { archiveClient } = useArchive();
  const { linksById } = useData();
  const clientes = clientsList;
  // Freeze panes: the Empresa column stays put while the rest scrolls sideways.
  const frozen = useFrozenColumns(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedClientForAssignment, setSelectedClientForAssignment] = useState(null);
  const [notasDialogOpen, setNotasDialogOpen] = useState(false);
  const [selectedClientForNotas, setSelectedClientForNotas] = useState(null);
  const [editedNotas, setEditedNotas] = useState("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedClientForArchive, setSelectedClientForArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [newClient, setNewClient] = useState({
    nombreEmpresa: "",
    rfc: "",
    nombreContacto: "",
    telefonoContacto: "",
    correoContacto: "",
    notas: "",
  });

  const isLoading = clientsLoading || userLoading;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClient((prev) => ({
      ...prev,
      [name]: name === "rfc" ? value.toUpperCase().replace(/\s/g, "") : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const clientData = {
        ...newClient,
        createdAt: new Date(),
        createdBy: user?.uid,
      };

      if (userData?.org) {
        // Org-based client creation
        clientData.org = userData.org;
      } else {
        // LEGACY: Fallback for unmigrated users using usuario[] array
        const usuarioArray = [user?.uid];

        // Add superiores (bosses) if user is a subordinate
        if (userData?.superiores && Array.isArray(userData.superiores)) {
          usuarioArray.push(...userData.superiores);
        }

        // For subordinates, set createdBy to their boss (first superior)
        if (userData?.superiores && userData.superiores.length > 0) {
          clientData.createdBy = userData.superiores[0];
        }

        clientData.usuario = usuarioArray;
      }

      await addClient(clientData);

      setNewClient({
        nombreEmpresa: "",
        rfc: "",
        nombreContacto: "",
        telefonoContacto: "",
        correoContacto: "",
        notas: "",
      });
      setIsAddingClient(false);
    } catch (error) {
      console.error("Error adding client:", error);
    }
  };

  const handleRowClick = (cliente) => {
    navigate(`/app/clientes/${cliente.id}`);
  };

  const handleOpenNotasDialog = (cliente, e) => {
    e.stopPropagation();
    setSelectedClientForNotas(cliente);
    setEditedNotas(cliente.notas || "");
    setNotasDialogOpen(true);
  };

  const handleSaveNotas = async () => {
    if (!selectedClientForNotas) return;

    try {
      const oldNotas = selectedClientForNotas.notas || "";
      const newNotas = editedNotas;

      // Only update if there's a change
      if (oldNotas === newNotas) {
        setNotasDialogOpen(false);
        setSelectedClientForNotas(null);
        return;
      }

      const clienteRef = doc(db, "clientes", selectedClientForNotas.id);
      await updateDoc(clienteRef, { notas: newNotas });

      // Log to bitacora
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: 'clientes',
          documentId: selectedClientForNotas.id,
          changes: [{
            field: 'notas',
            oldValue: oldNotas,
            newValue: newNotas,
          }],
          metadata: {
            clientId: selectedClientForNotas.id,
            clientName: selectedClientForNotas.nombreEmpresa || 'Sin nombre',
            description: `Actualizó notas del cliente. Anterior: "${oldNotas || '(vacío)'}" → Nueva: "${newNotas || '(vacío)'}"`,
          },
          user,
          userData,
        });
      } catch (logError) {
        console.error('Failed to log notas update to bitacora:', logError);
      }

      setNotasDialogOpen(false);
      setSelectedClientForNotas(null);
    } catch (error) {
      console.error("Error updating notas:", error);
    }
  };

  const handleArchiveClient = async () => {
    const client = selectedClientForArchive;
    if (!client) return;
    setArchiving(true);
    try {
      await archiveClient(client.id);
      try {
        await logBitacora({
          action: "UPDATE",
          collection: "clientes",
          documentId: client.id,
          changes: [{ field: "archived", oldValue: false, newValue: true }],
          metadata: {
            clientId: client.id,
            clientName: client.nombreEmpresa || "Sin nombre",
            description: `Archivó cliente "${client.nombreEmpresa || "Sin nombre"}"`,
          },
          user,
          userData,
        });
      } catch (logErr) {
        console.error("Failed to log archive to bitacora:", logErr);
      }
    } catch (err) {
      console.error("Error archiving client:", err);
    } finally {
      setArchiving(false);
      setArchiveDialogOpen(false);
      setSelectedClientForArchive(null);
    }
  };

  // Filter clients based on search term
  const filteredClientes = clientes.filter(cliente => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return [cliente.nombreEmpresa, cliente.nombreContacto, cliente.telefonoContacto, cliente.rfc, cliente.notas]
      .some(v => texto(v).includes(search));
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredClientes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedClientes = filteredClientes.slice(startIndex, endIndex);

  // Reset to first page when search or items per page changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Heading level={1} className="text-3xl font-bold bg-clip-text text-azul-surelia mb-2">
            Gestión de Clientes
          </Heading>
        </div>
        <div className="flex items-center gap-2">
          <Button outline color="surelia" onClick={() => navigate("/app/clientes/archivados")}>
            <ArchiveBoxIcon className="w-4 h-4 mr-2" />
            Archivados
          </Button>
          <Button onClick={() => setIsAddingClient(true)} color="surelia">
            Agregar Cliente
          </Button>
        </div>
      </div>

      {/* Search Bar and Items Per Page Selector */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <Input
          type="text"
          placeholder="Buscar por empresa, RFC, contacto, teléfono o notas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 sm:max-w-md w-full border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
        />

        <div className="flex items-center gap-2 whitespace-nowrap">
          <label className="text-sm font-medium text-azul-surelia">Mostrar:</label>
          <select
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            className="px-[calc(theme(spacing[3])-1px)] py-[calc(theme(spacing[1.5])-1px)] border border-azul-surelia/30 rounded-lg text-azul-surelia bg-white focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none text-sm/6 min-w-[60px]"
          >
            <option value={5}>5</option>
            <option value={8}>8</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <span className="text-sm text-azul-surelia/70">por página</span>
        </div>
      </div>

      <Dialog open={isAddingClient} onClose={() => setIsAddingClient(false)} size="md">
        <DialogTitle>Agregar Nuevo Cliente</DialogTitle>
        
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <Field>
            <Label className="text-azul-surelia font-semibold">Nombre de la Empresa</Label>
            <Input
              name="nombreEmpresa"
              value={newClient.nombreEmpresa}
              onChange={handleInputChange}
              required
              autoComplete="off"
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
            />
          </Field>

          <Field>
            <Label className="text-azul-surelia font-semibold">RFC</Label>
            <Input
              name="rfc"
              value={newClient.rfc}
              onChange={handleInputChange}
              placeholder="XAXX010101000"
              maxLength="13"
              autoComplete="off"
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 uppercase"
            />
          </Field>

          <Field>
            <Label className="text-azul-surelia font-semibold">Nombre del Contacto</Label>
            <Input
              name="nombreContacto"
              value={newClient.nombreContacto}
              onChange={handleInputChange}
              required
              autoComplete="off"
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
            />
          </Field>

          <Field>
            <Label className="text-azul-surelia font-semibold">Teléfono del Contacto</Label>
            <Input
              type="tel"
              name="telefonoContacto"
              value={newClient.telefonoContacto}
              onChange={handleInputChange}
              required
              autoComplete="off"
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
            />
          </Field>

          <Field>
            <Label className="text-azul-surelia font-semibold">Correo del Contacto</Label>
            <Input
              type="email"
              name="correoContacto"
              value={newClient.correoContacto}
              onChange={handleInputChange}
              required
              autoComplete="off"
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
            />
          </Field>

          <Field>
            <Label className="text-azul-surelia font-semibold">Notas</Label>
            <Textarea
              name="notas"
              value={newClient.notas}
              onChange={handleInputChange}
              rows={3}
              autoComplete="off"
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20"
            />
          </Field>

          <div className="flex justify-end space-x-3 pt-4 border-t border-azul-surelia/10">
            <Button
              outline
              color="surelia"
              onClick={() => setIsAddingClient(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" color="surelia">
              Guardar Cliente
            </Button>
          </div>
        </form>
      </Dialog>

      {filteredClientes.length > 0 ? (
        <div className="mt-6">
          {/* Modern Table with Surelia styling */}
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <div className="relative">
              <Table onScroll={frozen.onScroll}>
                <TableHead className="bg-azul-surelia">
                  <TableRow>
                    <TableHeader
                      ref={frozen.setRef(0)}
                      style={{ left: frozen.offsets[0] }}
                      className={`text-white font-semibold py-4 border-b-0 ${frozenHeadClass}`}
                    >Empresa</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Docs</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Responsables</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">RFC</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Contacto</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Teléfono</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Correo</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Notas</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0">Creado por</TableHeader>
                    <TableHeader className="text-white font-semibold py-4 border-b-0"></TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedClientes.map((cliente, index) => {
                    const expiryStatus = getClientExpiryStatus(cliente.links, linksById);
                    const identityStatus = getClientIdentityStatus(cliente, cliente.links, linksById);
                    return (
                    <TableRow
                      key={cliente.id}
                      onClick={() => handleRowClick(cliente)}
                      className={`group cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                      }`}
                    >
                      <TableCell
                        style={{ left: frozen.offsets[0] }}
                        className={`font-semibold text-azul-surelia py-4 ${frozenCellClass(index)}`}
                      >
                        {cliente.nombreEmpresa}
                      </TableCell>
                      <TableCell className="py-4">
                        {expiryStatus.worst === 'expired' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium"
                            title={`${expiryStatus.expiredCount} documento${expiryStatus.expiredCount !== 1 ? 's' : ''} vencido${expiryStatus.expiredCount !== 1 ? 's' : ''}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                            {expiryStatus.expiredCount}
                          </span>
                        ) : expiryStatus.worst === 'warning' ? (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium"
                            title={`${expiryStatus.warningCount} documento${expiryStatus.warningCount !== 1 ? 's' : ''} por vencer`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                            {expiryStatus.warningCount}
                          </span>
                        ) : (cliente.links?.length > 0 && Object.keys(linksById).length > 0) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                            OK
                          </span>
                        ) : null}
                        {identityStatus.mismatchCount > 0 && (
                          <span
                            className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold"
                            title={`Datos que no coinciden en ${identityStatus.mismatchCount} documento${identityStatus.mismatchCount !== 1 ? 's' : ''}`}
                          >
                            !
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center gap-2">
                          <AssignedUsersBubbles
                            userIds={cliente.assignedUsers || []}
                            maxDisplay={3}
                            size="sm"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedClientForAssignment(cliente);
                              setAssignmentDialogOpen(true);
                            }}
                            className="p-1.5 rounded-lg text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title="Editar responsables"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-700 py-4 font-mono text-sm">
                        {cliente.rfc ? (
                          <span className="inline-flex items-center px-2 py-1 rounded bg-azul-surelia/10 text-azul-surelia">
                            {cliente.rfc}
                          </span>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-700 py-4">{cliente.nombreContacto}</TableCell>
                      <TableCell className="text-cian-digital font-medium py-4">{cliente.telefonoContacto}</TableCell>
                      <TableCell className="text-gray-600 text-sm py-4">{cliente.correoContacto}</TableCell>
                      <TableCell className="text-gray-600 text-sm py-4">
                        <div className="flex items-center gap-2">
                          <span className="flex-1">
                            {cliente.notas || <span className="text-gray-400">--</span>}
                          </span>
                          <button
                            onClick={(e) => handleOpenNotasDialog(cliente, e)}
                            className="p-1.5 rounded-lg text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200 flex-shrink-0"
                            title="Editar notas"
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-azul-surelia/10 text-azul-surelia text-xs font-medium">
                          {getUserEmail(cliente.createdBy)}
                        </span>
                      </TableCell>
                      <TableCell className="py-4">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedClientForArchive(cliente);
                            setArchiveDialogOpen(true);
                          }}
                          className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200"
                          title="Archivar cliente"
                        >
                          <ArchiveBoxIcon className="w-4 h-4" />
                        </button>
                      </TableCell>
                </TableRow>
                    );
                  })}
            </TableBody>
              </Table>
              {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
                  pinned to the boundary instead of riding along with the cells. */}
              {frozen.scrolled && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
                  style={{ left: frozen.dividerLeft, bottom: frozen.dividerBottom }}
                />
              )}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-beige-suave/10 px-4 sm:px-6 py-4 border-t border-azul-surelia/10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm text-azul-surelia/70 text-center sm:text-left">
                    Mostrando {startIndex + 1} - {Math.min(endIndex, filteredClientes.length)} de {filteredClientes.length} clientes
                  </div>
                  
                  <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                    <Button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      outline
                      color="surelia"
                      className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                    >
                      Anterior
                    </Button>
                    
                    <div className="flex items-center gap-0.5 sm:gap-1">
                      {(() => {
                        const pageNumbers = [];
                        const maxVisiblePages = 5;
                        
                        if (totalPages <= maxVisiblePages) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            pageNumbers.push(i);
                          }
                        } else {
                          // Always show first page
                          pageNumbers.push(1);
                          
                          if (currentPage <= 2) {
                            // Show first few pages
                            for (let i = 2; i <= Math.min(3, totalPages - 1); i++) {
                              pageNumbers.push(i);
                            }
                            if (totalPages > 3) pageNumbers.push('...');
                          } else if (currentPage >= totalPages - 1) {
                            // Show last few pages
                            if (totalPages > 3) pageNumbers.push('...');
                            for (let i = Math.max(totalPages - 2, 2); i <= totalPages - 1; i++) {
                              pageNumbers.push(i);
                            }
                          } else {
                            // Show pages around current page
                            pageNumbers.push('...');
                            pageNumbers.push(currentPage);
                            pageNumbers.push('...');
                          }
                          
                          // Always show last page
                          if (totalPages > 1) pageNumbers.push(totalPages);
                        }
                        
                        return pageNumbers.map((page, index) => 
                          page === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-1 sm:px-2 py-1 text-azul-surelia/50 text-xs sm:text-sm">
                              ...
                            </span>
                          ) : (
                            <Button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              color="surelia"
                              outline={page !== currentPage}
                              className="min-w-[32px] sm:min-w-[40px] text-xs sm:text-sm px-1.5 sm:px-2 py-1 sm:py-2"
                            >
                              {page}
                            </Button>
                          )
                        );
                      })()}
                    </div>
                    
                    <Button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      outline
                      color="surelia"
                      className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg border border-azul-surelia/20 p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-azul-surelia/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlusIcon className="w-8 h-8 text-azul-surelia" />
            </div>
            <Text variant="secondary" className="text-lg mb-4 text-azul-surelia">
              {searchTerm ? (
                `No se encontraron clientes que coincidan con "${searchTerm}".`
              ) : clientes.length === 0 ? (
                "No hay clientes registrados."
              ) : (
                "No se encontraron clientes que coincidan con la búsqueda."
              )}
            </Text>
            {(!searchTerm && clientes.length === 0) && (
              <Button 
                onClick={() => setIsAddingClient(true)} 
                className="bg-verde-lima hover:bg-verde-lima/90 text-azul-surelia font-semibold"
              >
                <UserPlusIcon className="w-4 h-4 mr-2" />
                Agregar tu primer cliente
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Assignment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => {
          setAssignmentDialogOpen(false);
          setSelectedClientForAssignment(null);
        }}
        size="md"
      >
        <div className="p-1">
          <Heading level={3} className="text-xl font-bold text-azul-surelia mb-1">
            Asignar Responsables
          </Heading>
          <Text className="text-sm text-gray-500 mb-4">
            {selectedClientForAssignment?.nombreEmpresa}
          </Text>
          {selectedClientForAssignment && (
            <UserAssignmentSelector
              assignedUsers={selectedClientForAssignment.assignedUsers || []}
              onSave={async (userIds) => {
                const oldUsers = selectedClientForAssignment.assignedUsers || [];
                const { description, changes, hasChanges } = computeUserAssignmentChanges(
                  oldUsers,
                  userIds,
                  getUserName
                );

                await updateClientAssignedUsers(selectedClientForAssignment.id, userIds);

                if (hasChanges) {
                  try {
                    await logBitacora({
                      action: 'UPDATE',
                      collection: 'clientes',
                      documentId: selectedClientForAssignment.id,
                      changes,
                      metadata: {
                        clientId: selectedClientForAssignment.id,
                        clientName: selectedClientForAssignment.nombreEmpresa || 'Sin nombre',
                        description,
                      },
                      user,
                      userData,
                    });
                  } catch (logError) {
                    console.error('Failed to log user assignment to bitacora:', logError);
                  }
                }

                setAssignmentDialogOpen(false);
                setSelectedClientForAssignment(null);
              }}
              onCancel={() => {
                setAssignmentDialogOpen(false);
                setSelectedClientForAssignment(null);
              }}
            />
          )}
        </div>
      </Dialog>

      {/* Notas Edit Dialog */}
      <Dialog
        open={notasDialogOpen}
        onClose={() => {
          setNotasDialogOpen(false);
          setSelectedClientForNotas(null);
        }}
        size="md"
      >
        <div className="p-1">
          <Heading level={3} className="text-xl font-bold text-azul-surelia mb-1">
            Editar Notas
          </Heading>
          <Text className="text-sm text-gray-500 mb-4">
            {selectedClientForNotas?.nombreEmpresa}
          </Text>
          <Field>
            <Textarea
              value={editedNotas}
              onChange={(e) => setEditedNotas(e.target.value)}
              rows={4}
              placeholder="Escribe las notas del cliente..."
              className="border-azul-surelia/30 focus:border-azul-surelia focus:ring-azul-surelia/20 w-full"
            />
          </Field>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              outline
              color="surelia"
              onClick={() => {
                setNotasDialogOpen(false);
                setSelectedClientForNotas(null);
              }}
            >
              Cancelar
            </Button>
            <Button color="surelia" onClick={handleSaveNotas}>
              Guardar
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => !archiving && setArchiveDialogOpen(false)}
        size="sm"
      >
        <DialogTitle>Archivar Cliente</DialogTitle>
        <div className="mt-4">
          <Text>
            ¿Estás seguro de que deseas archivar a{" "}
            <strong>{selectedClientForArchive?.nombreEmpresa}</strong>? El cliente y sus
            contratos/fianzas se ocultarán de las vistas principales. Podrás consultarlos en la
            sección de archivados.
          </Text>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button
            outline
            color="surelia"
            onClick={() => {
              setArchiveDialogOpen(false);
              setSelectedClientForArchive(null);
            }}
            disabled={archiving}
          >
            Cancelar
          </Button>
          <Button color="red" onClick={handleArchiveClient} disabled={archiving}>
            {archiving ? "Archivando..." : "Archivar"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
};

export default Clientes;
